import * as React from "react";
import dayjs from "dayjs";
import { useTheme, styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PersonIcon from "@mui/icons-material/Person";
import { useTitle } from "../../hooks/useTitle";
import { useUILabel } from "../../hooks/useUILabel";
import ProjectDetailsPanel from "./ProjectDetailsPanel";
import ProjectHistoriesPanel from "./ProjectLogsPanel";
import ProjectMessagesPanel from "./ProjectMessagesPanel";
import ProjectTalentsPanel from "./ProjectTalentsPanel";
import {
  NegotiationStatus,
  Project,
  ProjectStatus,
  ReportProgress,
  ReportProgressStatus,
} from "../../types/project";
import {
  useCancelProjectMutation,
  useCompleteProjectMutation,
  useGetProjectByIdQuery,
  usePublishProjectMutation,
} from "../../services/project.service";
import {
  Alert,
  Backdrop,
  ButtonGroup,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
} from "@mui/material";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import { toast } from "react-toastify";
import { AttachMoney, Check, Close, Edit, Send } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import ConfirmModal from "../../components/ConfirmModal";
import { getLastProgress } from "../../utils/projectHelper";
import ModalCancelProject from "./ModalCancelProject";
import qs from "qs";
import ModalInvite from "./ModalInvite";
import { formatWithTimezone } from "../../utils/dateHelper";
import { CardBase } from "../../components/global";

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

const StyledTabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  minHeight: "400px",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2.5, 2.5, 3),
    minHeight: "auto",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 0, 2),
    minHeight: "auto",
  },
}));

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <StyledTabPanel>{children}</StyledTabPanel>}
    </div>
  );
}

const StyledMetaButton = styled(Button)(({ theme }) => ({
  textTransform: "unset",
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(2),
  padding: theme.spacing(0.75, 1.5),
  borderRadius: 8,
  backgroundColor: theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.05)"
    : "rgba(0, 0, 0, 0.03)",
  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"}`,
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.06)",
    transform: "translateY(-1px)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 4px 12px rgba(0, 0, 0, 0.3)"
      : "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  "& .MuiButton-startIcon": {
    marginRight: theme.spacing(0.75),
  },
  [theme.breakpoints.down("sm")]: {
    marginRight: theme.spacing(0.5),
    marginBottom: 0,
    padding: theme.spacing(0.5, 1),
    fontSize: "0.875rem",
  },
}));

const ProjectMeta = ({
  icon,
  value,
}: {
  icon: React.ReactNode;
  value: string;
}) => (
  <StyledMetaButton variant="text" startIcon={icon} size="small">
    {value}
  </StyledMetaButton>
);

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.75rem",
  height: 28,
  boxShadow: theme.palette.mode === "dark"
    ? "0 2px 4px rgba(0, 0, 0, 0.3)"
    : "0 2px 4px rgba(0, 0, 0, 0.1)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 4px 8px rgba(0, 0, 0, 0.4)"
      : "0 4px 8px rgba(0, 0, 0, 0.15)",
  },
}));

const StatusChip = React.memo(({ status }: { status?: string; }) => {
  const draftStatusLabel = useUILabel("project.draftStatus");
  const publishedStatusLabel = useUILabel("project.publishedStatus");
  const scheduledStatusLabel = useUILabel("project.scheduledStatus");
  const inProgressStatusLabel = useUILabel("project.inProgressStatus");
  const completedStatusLabel = useUILabel("project.completedStatus");
  const cancelledStatusLabel = useUILabel("project.cancelledStatus");
  
  if (status == ProjectStatus.DRAFT)
    return <StyledChip label={draftStatusLabel} color="default" />;
  if (status == ProjectStatus.PUBLISHED)
    return <StyledChip label={publishedStatusLabel} color="secondary" />;
  if (status == ProjectStatus.SCHEDULED)
    return <StyledChip label={scheduledStatusLabel} color="primary" />;
  if (status == ProjectStatus.IN_PROGRESS)
    return <StyledChip label={inProgressStatusLabel} color="primary" />;
  if (status == ProjectStatus.COMPLETED)
    return <StyledChip label={completedStatusLabel} color="info" />;
  if (status == ProjectStatus.CANCELED)
    return <StyledChip label={cancelledStatusLabel} color="error" />;
  else return <StyledChip label={status} color="default" />;
});

const StyledHeaderBox = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === "dark"
    ? "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)"
    : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  borderRadius: 16,
  padding: theme.spacing(3, 4),
  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)"}`,
  boxShadow: theme.palette.mode === "dark"
    ? "0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
    : "0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
  marginBottom: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}60, transparent)`,
    opacity: 0.6,
  },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2.5, 3),
    borderRadius: 14,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 1.5),
    borderRadius: 12,
    marginBottom: theme.spacing(2),
  },
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.75rem",
  color: theme.palette.text.primary,
  letterSpacing: "-0.02em",
  marginLeft: theme.spacing(1.5),
  lineHeight: 1.3,
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.15rem",
    marginLeft: 0,
    lineHeight: 1.2,
  },
}));

const StyledActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 10,
  padding: theme.spacing(1, 2.5),
  fontWeight: 600,
  textTransform: "none",
  boxShadow: theme.palette.mode === "dark"
    ? "0 4px 12px rgba(0, 0, 0, 0.3)"
    : "0 4px 12px rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 6px 20px rgba(0, 0, 0, 0.4)"
      : "0 6px 20px rgba(0, 0, 0, 0.15)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.75, 1.5),
    fontSize: "0.875rem",
  },
}));

const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
  borderRadius: 10,
  padding: theme.spacing(1, 2.5),
  fontWeight: 600,
  textTransform: "none",
  boxShadow: theme.palette.mode === "dark"
    ? "0 4px 12px rgba(0, 0, 0, 0.3)"
    : "0 4px 12px rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 6px 20px rgba(0, 0, 0, 0.4)"
      : "0 6px 20px rgba(0, 0, 0, 0.15)",
  },
  "&.Mui-disabled": {
    opacity: 0.5,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.75, 1.5),
    fontSize: "0.875rem",
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.05)"
    : "rgba(0, 0, 0, 0.02)",
  borderRadius: "16px 16px 0 0",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  padding: theme.spacing(0.5, 1),
  [theme.breakpoints.down("md")]: {
    borderRadius: "14px 14px 0 0",
    padding: theme.spacing(0.5, 0.75),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.5, 0.5, 0),
    borderRadius: "12px 12px 0 0",
    overflowX: "auto",
    scrollbarWidth: "thin",
  },
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.95rem",
    minHeight: 56,
    color: theme.palette.text.secondary,
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    [theme.breakpoints.down("md")]: {
      minHeight: 52,
      fontSize: "0.9rem",
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 44,
      fontSize: "0.85rem",
      paddingInline: theme.spacing(1),
    },
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.03)",
    },
    "&.Mui-selected": {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
  },
  "& .MuiTabs-indicator": {
    height: 3,
    borderRadius: "3px 3px 0 0",
    backgroundColor: theme.palette.primary.main,
  },
}));

const ProjectHeader = ({ data }: { data?: Project; }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isTabletDown = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const editLabel = useUILabel("common.edit");
  const changeWorkManagerLabel = useUILabel("project.changeWorkManager");
  const publishWorkLabel = useUILabel("project.publishWork");
  const hourlyLabel = useUILabel("project.hourly");
  const fixedRateLabel = useUILabel("project.fixedRate");
  const workPublishedLabel = useUILabel("project.workPublished");
  const workCompletedLabel = useUILabel("project.workCompleted");
  const cancelWorkLabel = useUILabel("project.cancelWork");
  const completeWorkLabel = useUILabel("project.completeWork");
  const publishWorkQuestionLabel = useUILabel("project.publishWorkQuestion");
  const completeWorkQuestionLabel = useUILabel("project.completeWorkQuestion");
  const privatePostingLabel = useUILabel("project.privatePosting");
  const publicPostingLabel = useUILabel("project.publicPosting");
  const inviteIndependentProfessionalLabel = useUILabel("project.inviteIndependentProfessional");

  const [confirmPublishModalOpen, setConfirmPublishModalOpen] =
    React.useState(false);
  const [cancelModal, setCancelModal] = React.useState<{
    visible: boolean;
    project: Project | null;
  }>({
    visible: false,
    project: null,
  });
  const [confirmCompleteModalOpen, setConfirmCompleteModalOpen] =
    React.useState(false);

  const [publish, { isLoading: publishing }] = usePublishProjectMutation();
  const [cancel, { isLoading: canceling }] = useCancelProjectMutation();
  const [complete, { isLoading: completing }] = useCompleteProjectMutation();
  const [showModalInvite, setShowModalInvite] = React.useState(false);
  const [showFav, setShowFav] = React.useState(false);

  const canCancelProject = React.useMemo(
    () =>
      [
        ProjectStatus.DRAFT,
        ProjectStatus.PUBLISHED,
        ProjectStatus.BUDGET_APPROVAL,
      ].includes(data?.status!),
    [data]
  );

  const canCompleteProject = React.useMemo(() => {
    // check that all progress is approved
    const hired = data?.resources?.filter((it) => !!it.hired_at) || [];
    if (hired.length > 0) {
      const approved = hired.filter((it) => {
        const progress = getLastProgress(it);
        return progress?.status == ReportProgressStatus.APPROVED;
      });
      return approved.length == hired.length;
    } else {
      // no one hired yet
      return false;
    }
  }, [data]);

  const { city, state, zip } = React.useMemo(() => {
    if (!!data?.customer) {
      return data?.customer;
    } else if (!!data?.location) {
      return data?.location;
    } else
      return {
        city: "?",
        state: "?",
        zip: "?",
      };
  }, [data]);

  const handlePublish = React.useCallback(async () => {
    try {
      if (!data?.id) return;
      await publish(data?.id).unwrap();
      toast.success(workPublishedLabel);
    } catch (err: any) {
      toast.error(err);
    }
  }, [data]);

  // const handleCancel = React.useCallback(async () => {
  //   try {
  //     if (!data?.id) return;
  //     await cancel(data?.id).unwrap();
  //     toast.success("Work canceled");
  //   } catch (err: any) {
  //     toast.error(err);
  //   }
  // }, [data]);
  const isAcceptingBid = React.useMemo(
    () => data?.status == ProjectStatus.PUBLISHED,
    [data]
  );

  const resource = React.useMemo(() => {
    const res = data?.resources?.filter(
      (it) =>
        it.status == NegotiationStatus.HIRED ||
        it.status == NegotiationStatus.COMPLETED
    )?.[0];
    return res;
  }, [data]);

  const handleComplete = React.useCallback(async () => {
    try {
      if (!data?.id) return;
      await complete(data?.id).unwrap();
      toast.success(workCompletedLabel);
    } catch (err: any) {
      toast.error(err);
    }
  }, [data]);

  return (
    <>
      <StyledHeaderBox>
        <Stack
          direction={isTabletDown ? "column" : "row"}
          alignItems={isTabletDown ? "stretch" : "flex-start"}
          justifyContent="space-between"
          spacing={isTabletDown ? (isMobile ? 1.5 : 2) : 0}
        >
          <Stack direction="column" flex={1} spacing={isMobile ? 1 : 2}>
            <Stack
              direction={isMobile ? "column" : "row"}
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 0.5 : 0.75}
              sx={{ mb: isMobile ? 0.5 : 2 }}
            >
              <StatusChip status={data?.status} />
              <StyledTitle>{`#${data?.id || ""} ${data?.title || ""}`}</StyledTitle>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={isMobile ? 0.75 : 1.5}
            >
              <ProjectMeta
                icon={<BusinessCenterIcon />}
                value={!!data?.is_private ? privatePostingLabel : publicPostingLabel}
              />
              <ProjectMeta
                icon={<AttachMoney />}
                value={data?.type == "hourly" ? hourlyLabel : fixedRateLabel}
              />
              <ProjectMeta
                icon={<CalendarMonthIcon />}
                value={
                  !!resource &&
                    (data?.status == ProjectStatus.IN_PROGRESS ||
                      data?.status == ProjectStatus.SCHEDULED ||
                      data?.status == ProjectStatus.COMPLETED)
                    ? formatWithTimezone(resource?.start_date, resource?.timezone)
                    : formatWithTimezone(data?.start_date, data?.timezone)
                }
              />
              <ProjectMeta
                icon={<PersonIcon />}
                value={`${data?.num_of_talent || "#"} IP(s)`}
              />
            </Stack>
            {data?.status == ProjectStatus.CANCELED && (
              <Alert severity="error" sx={{ mt: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
                Cancel Reason: {data?.canceled_reason || "n/a"}
              </Alert>
            )}
          </Stack>
          <Stack
            direction="row"
            gap={isMobile ? 0.75 : 1.5}
            flexWrap="wrap"
            sx={{ ml: isTabletDown ? 0 : 2 }}
          >
            {isAcceptingBid && (
              <StyledActionButton
                variant="contained"
                color="info"
                onClick={() => setShowModalInvite(true)}
              >
                {inviteIndependentProfessionalLabel}
              </StyledActionButton>
            )}
            {[
              ProjectStatus.DRAFT,
              ProjectStatus.PUBLISHED,
              ProjectStatus.SCHEDULED,
              ProjectStatus.IN_PROGRESS,
            ].includes(data?.status!) && (
                <StyledActionButton
                  variant="contained"
                  color="success"
                  onClick={() => navigate(`/works/${data?.id}/edit`)}
                  startIcon={<Edit />}
                >
                  {data?.status === ProjectStatus.IN_PROGRESS ||
                    data?.status === ProjectStatus.SCHEDULED
                    ? changeWorkManagerLabel
                    : editLabel}
                </StyledActionButton>
              )}
            {[ProjectStatus.DRAFT].includes(data?.status!) && (
              <StyledLoadingButton
                variant="contained"
                color="info"
                onClick={() => setConfirmPublishModalOpen(true)}
                startIcon={<Send />}
                loading={publishing}
              >
                {publishWorkLabel}
              </StyledLoadingButton>
            )}

            {canCancelProject && (
              <StyledLoadingButton
                variant="contained"
                onClick={() =>
                  setCancelModal({ visible: true, project: data || null })
                }
                startIcon={<Close />}
                color="error"
                loading={canceling}
              >
                {cancelWorkLabel}
              </StyledLoadingButton>
            )}
            {[ProjectStatus.IN_PROGRESS].includes(data?.status!) && (
              <Stack>
                <StyledLoadingButton
                  variant="contained"
                  onClick={() => setConfirmCompleteModalOpen(true)}
                  startIcon={<Check />}
                  disabled={!canCompleteProject}
                  loading={completing}
                >
                  {completeWorkLabel}
                </StyledLoadingButton>
                {!canCompleteProject && (
                  <Typography variant="caption" sx={{ mt: 0.5, fontSize: "0.7rem" }}>
                    Please approve all Independent Professional's work before
                    completing.
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </StyledHeaderBox>
      <ConfirmModal
        open={confirmPublishModalOpen}
        title={publishWorkQuestionLabel}
        onClose={() => setConfirmPublishModalOpen(false)}
        onConfirm={handlePublish}
      />
      <ModalCancelProject
        open={cancelModal.visible}
        onClose={() => setCancelModal({ visible: false, project: null })}
        project={cancelModal.project}
      />
      <ConfirmModal
        open={confirmCompleteModalOpen}
        title={completeWorkQuestionLabel}
        onClose={() => setConfirmCompleteModalOpen(false)}
        onConfirm={handleComplete}
      />
      {data && (
        <ModalInvite
          open={showModalInvite}
          onClose={() => {
            setShowFav(false);
            setShowModalInvite(false);
          }}
          project={data!}
          showFav={showFav}
          setShowFav={setShowFav}
        />
      )}
    </>
  );
};

function a11yProps(index: number) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const overviewLabel = useUILabel("project.overview");
  const independentProfessionalsLabel = useUILabel("talent.independentProfessionals");
  const chatsLabel = useUILabel("project.chats");
  const editLabel = useUILabel("common.edit");
  const changeWorkManagerLabel = useUILabel("project.changeWorkManager");
  const publishWorkLabel = useUILabel("project.publishWork");

  const theme = useTheme();
  const isTabletDown = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const { data, isLoading, isFetching } = useGetProjectByIdQuery(
    parseInt(projectId!),
    {
      skip: !projectId,
    }
  );

  useTitle(`Work ID#${data?.id}`, `${data?.title}`);

  React.useEffect(() => {
    const searchParams = window.location.search;
    if (searchParams) {
      const params = qs.parse(searchParams.substring(1));
      if (params.t && typeof params.t === "string") {
        const tabIndex = Number(params.t);
        if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 2) {
          setValue(tabIndex);
        }
      }
    }
  }, []);

  return (
    <Box
      sx={{
        px: 0,
        py: { xs: 1, md: 2 },
      }}
    >
      <ProjectHeader data={data} />
      {(isLoading || isFetching) && (
        <LinearProgress
          sx={{
            height: 3,
            borderRadius: "0 0 4px 4px",
            backgroundColor: theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.1)",
            "& .MuiLinearProgress-bar": {
              borderRadius: "0 0 4px 4px",
            },
          }}
        />
      )}
      <CardBase useGlassCard={true} nested={false} sx={{ mt: { xs: 2, md: 3 }, overflow: "hidden" }}>
        <StyledTabs
          value={value}
          onChange={handleChange}
          variant={isTabletDown ? "scrollable" : "fullWidth"}
          scrollButtons={isTabletDown ? "auto" : false}
          allowScrollButtonsMobile
        >
          <Tab label={overviewLabel} {...a11yProps(0)} />
          <Tab label={independentProfessionalsLabel} {...a11yProps(1)} />
          <Tab label={chatsLabel} {...a11yProps(2)} />
        </StyledTabs>
        <TabPanel value={value} index={0} dir={theme.direction}>
          <ProjectDetailsPanel data={data} />
        </TabPanel>
        <TabPanel value={value} index={1} dir={theme.direction}>
          {!!data && <ProjectTalentsPanel project={data} />}
        </TabPanel>
        <TabPanel value={value} index={2} dir={theme.direction}>
          {!!data && <ProjectMessagesPanel project={data} />}
        </TabPanel>
      </CardBase>
    </Box>
  );
};
export default ProjectDetailPage;
